import { ImageFormat, ImageUrlOptions } from '../../../../types';

/*
 * Storage serves pictures through Google's image service (`lh3.googleusercontent.com/<id>`),
 * also proxied by our API under `/storage/public/<id>` on any environment's host.
 * Options are a `=`-suffix on the path, dash-separated (`<id>=w200-h100-n-rw`), not query params.
 * Without a suffix the service caps the image at 512 px; the limit for a resize is 1600 px.
 * Reference: Confluence "S3 Bucket keys" in FR8BOT TechDocs.
 */
const SERVICE_HOST_SUFFIX = '.googleusercontent.com';
const SERVICE_PROXY_PATH = '/storage/public/';
const MAX_SIDE_PX = 1600;
// Rounding to a step keeps neighbouring layouts on the same URL, so the CDN cache hits.
const SIDE_STEP_PX = 64;
const SERVICE_PROTOCOL = 'https:';
const MAX_QUALITY = 100;

const CROP_OPTION = { none: null, center: 'n', smart: 'p' } as const;

export function buildImageUrl(
  src: string | null | undefined,
  options: ImageUrlOptions,
  pixelRatio = 1,
): string | null {
  if (!src) {
    return null;
  }

  const url = parseServiceUrl(src);

  if (!url) {
    return src;
  }

  const path = url.pathname.replace(/=[^/]*$/, '');
  url.pathname = `${path}=${serviceOptions(options, pixelRatio).join('-')}`;

  return url.toString();
}

function parseServiceUrl(src: string): URL | null {
  try {
    const url = new URL(src);

    const served =
      url.hostname.endsWith(SERVICE_HOST_SUFFIX) || url.pathname.startsWith(SERVICE_PROXY_PATH);

    return url.protocol === SERVICE_PROTOCOL && served ? url : null;
  } catch {
    return null;
  }
}

function serviceOptions(options: ImageUrlOptions, pixelRatio: number): string[] {
  if (options.original) {
    return ['s0'];
  }

  const px = (side: number) =>
    Math.min(MAX_SIDE_PX, Math.ceil((side * pixelRatio) / SIDE_STEP_PX) * SIDE_STEP_PX);
  const result: string[] = [];

  if (options.width || options.height) {
    if (options.width) result.push(`w${px(options.width)}`);
    if (options.height) result.push(`h${px(options.height)}`);
  } else if (options.size) {
    result.push(`s${px(options.size)}`);
  }

  const crop = CROP_OPTION[options.crop ?? 'none'];

  if (crop) result.push(crop);

  result.push('nu');

  if ((options.format ?? ImageFormat.Webp) === ImageFormat.Webp) result.push('rw');

  if (options.quality) {
    result.push(`l${Math.min(MAX_QUALITY, Math.max(1, Math.round(options.quality)))}`);
  }

  return result;
}
